/**
 * Oppgaver (#293, candidate 8): the work beside the Vaktplan. A grid of people by day (172 px owner, seven
 * 162 px days), a list by day, or a month calendar; one membership for the grid, the list, the counts and
 * the report (model.ts). The period, the view, the search and the status live in the address, so returning
 * from a task or reloading keeps them; a narrow screen opens on today as a list.
 */
import { useUsers } from '@/api/people';
import { useShiftsPlain } from '@/api/shifts';
import { type ApiTask, type GuideStep, useGuideProgress, useTaskWrites, useTasks } from '@/api/tasks';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { activityIcon, toneOf } from '@/pages/vaktplan/activityIcon';
import { oslo, today as todayOslo } from '@/rules';
import { Button, Drawer, Empty, LoadError, Skeleton, toast } from '@/ui';
import { dayMonth, fullName, shiftSpan } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { dow } from '@timefly/core';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TaskDetail } from './TaskDetail';
import { type FormOpen, TaskForm } from './TaskForm';
import {
  type Filter,
  SHOWN_LABEL,
  type Shown,
  type View,
  counts,
  dayHead,
  daysOf,
  estimateOf,
  monthText,
  onDay,
  periodOf,
  rangeText,
  reportCsv,
  shownOf,
  step,
  visible,
  whenOf,
} from './model';
import './oppgaver.css';

const narrowNow = () => typeof matchMedia === 'function' && matchMedia('(max-width: 1099px)').matches;
const STATUS_ICON: Record<Shown, IconName | null> = {
  done: 'check',
  exception: 'warn',
  late: 'clock',
  unassigned: 'user',
  open: null,
};

export default function Oppgaver() {
  const can = useCan();
  const mayEdit = can('vaktplan.endre');
  const today = todayOslo();
  const [sp, setSp] = useSearchParams();
  const narrow = narrowNow();
  const view = (sp.get('vis') as View | null) ?? (narrow ? 'day' : 'week');
  const date = sp.get('dato') ?? today;
  const layout = narrow ? 'list' : (sp.get('visning') ?? 'grid');
  const q = sp.get('sok') ?? '';
  const filter = (sp.get('status') as Filter | null) ?? 'all';
  const setParam = (patch: Record<string, string | null>) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v == null || v === '') next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );

  const { from, to } = periodOf(view, date);
  const tasksQ = useTasks(from, to);
  const shiftsQ = useShiftsPlain(from, to);
  const usersQ = useUsers();
  const w = useTaskWrites();
  const [form, setForm] = useState<FormOpen | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  const [help, setHelp] = useState(sp.get('panel') === 'hjelp');
  const { steps: guide, mark: markGuide } = useGuideProgress();

  const users = useMemo(
    () =>
      (usersQ.data ?? [])
        .filter((u) => u.active_status !== false)
        .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb')),
    [usersQ.data],
  );
  const shifts = shiftsQ.data?.shifts ?? [];
  const all = tasksQ.data ?? [];
  const shown = visible(all, q, filter, today);
  const n = counts(all, q, today);
  const doneShown = shown.filter((t) => t.status !== 'open').length;
  const days = daysOf(from, to);

  // the rows: nobody accountable first, then everyone with a shift or a task in the period
  const people = useMemo(() => {
    const ids = new Set<number>();
    for (const s of shifts) if (s.user_id != null) ids.add(s.user_id);
    for (const t of shown) if (t.assignee_id != null) ids.add(t.assignee_id);
    return users.filter((u) => ids.has(u.id));
  }, [users, shifts, shown]);

  // a task opened from a link (?oppgave=12) opens its detail once
  useEffect(() => {
    const id = Number(sp.get('oppgave'));
    if (id) setDetail(id);
  }, [sp]);

  const complete = async (t: ApiTask) => {
    if (!mayEdit) return setDetail(t.id);
    // proof first: a task that needs a comment or a value is completed in its detail
    if (t.status !== 'open' || t.requirement) return setDetail(t.id);
    try {
      await w.complete.mutateAsync({ id: t.id, body: { expected_revision: t.revision } });
      markGuide('follow');
      toast(T('«{title}» er utført', { title: t.title }), () => {
        void w.reopen.mutateAsync({ id: t.id, revision: t.revision + 1 });
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : T('Kunne ikke lagre.'));
    }
  };

  const saved = (t: ApiTask, created: boolean) => {
    setForm(null);
    if (created) {
      if (t.assignee_id != null) markGuide('create', 'assign');
      else markGuide('create');
      // the new task must be visible: its period, and no search or status hiding it
      setParam({ dato: t.start_date, sok: null, status: null });
      toast(T('Oppgaven er opprettet'));
    } else toast(T('Lagret'));
    setDetail(t.id);
  };

  const shiftFor = (person: number, day: string) =>
    shifts.filter((s) => s.user_id === person && oslo(s.start_time)?.date === day);

  const download = () => {
    const blob = new Blob([reportCsv(shown, today)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `oppgaver-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const taskLine = (t: ApiTask, withPerson = false) => {
    const s = shownOf(t, today);
    const icon = STATUS_ICON[s];
    const typeIcon = activityIcon(t.type?.name ?? t.title, false, t.type?.icon);
    return (
      <div className="o-task" key={t.id} data-testid="opg-task">
        <button
          type="button"
          className={`o-mark o-${t.status}`}
          aria-label={
            t.status === 'open'
              ? T('Marker «{title}» som utført', { title: t.title })
              : T('«{title}» er {status}. Åpne', { title: t.title, status: T(SHOWN_LABEL[s]).toLowerCase() })
          }
          onClick={() => complete(t)}
        >
          <i>{t.status !== 'open' && <Icon name={t.status === 'done' ? 'check' : 'warn'} />}</i>
        </button>
        <div className="o-tbody">
          <button type="button" className="o-ttl" onClick={() => setDetail(t.id)}>
            <span
              className="o-tic"
              style={toneOf(t.type?.color ?? null, t.type == null) as React.CSSProperties}
              aria-hidden="true"
            >
              <Icon name={typeIcon} />
            </span>
            {t.title}
          </button>
          <span className="o-tmeta">
            {[
              withPerson ? (t.assignee_name ?? T('Uten ansvarlig')) : null,
              whenOf(t),
              estimateOf(t.estimate_minutes),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {s !== 'open' && (
            <span className={`o-st o-${s}`}>
              {icon && <Icon name={icon} />}
              {T(SHOWN_LABEL[s])}
            </span>
          )}
          {t.assignee_id != null &&
            !t.shift_id &&
            s === 'open' &&
            shiftFor(t.assignee_id, t.start_date).length === 0 && (
              <span className="o-st"> {T('Uten vakt')}</span>
            )}
        </div>
      </div>
    );
  };

  const addBtn = (label: string, open: FormOpen) =>
    mayEdit ? (
      <button type="button" className="o-add" aria-label={label} title={label} onClick={() => setForm(open)}>
        <Icon name="plus" />
      </button>
    ) : null;

  let body: ReactNode;
  if (tasksQ.isError) body = <LoadError what={T('oppgavene')} onRetry={() => void tasksQ.refetch()} />;
  else if (tasksQ.isPending || usersQ.isPending) body = <Skeleton rows={5} />;
  else if (layout === 'grid' && view !== 'month') {
    const rows: { id: number | null; name: string }[] = [
      { id: null, name: T('Uten ansvarlig') },
      ...people.map((u) => ({ id: u.id, name: fullName(u) })),
    ];
    body = (
      <div className="o-grid" data-testid="opg-grid">
        <table style={{ minWidth: 172 + days.length * 132 }}>
          <colgroup>
            <col className="o-own" />
            {days.map((d) => (
              <col key={d} className="o-day" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">{T('Ansvarlig')}</th>
              {days.map((d) => (
                <th scope="col" key={d} className={d === today ? 'o-today' : ''}>
                  {dayHead(d)}
                  {d === today && <small>{T('I dag')}</small>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const mine = shown.filter((t) => t.assignee_id === r.id);
              return (
                <tr key={r.id ?? 'none'}>
                  <th scope="row" className="o-who">
                    <b>{r.name}</b>
                    <span>
                      {T('{done} av {all} utført', {
                        done: mine.filter((t) => t.status !== 'open').length,
                        all: mine.length,
                      })}
                    </span>
                  </th>
                  {days.map((d) => {
                    const here = mine.filter((t) => onDay(t, d));
                    const sh = r.id == null ? [] : shiftFor(r.id, d);
                    const head =
                      r.id == null
                        ? T('Uten ansvarlig')
                        : sh.length
                          ? sh
                              .map(
                                (s) =>
                                  `${shiftSpan(s.start_time, s.end_time)}${s.shiftType?.name ? ` · ${s.shiftType.name}` : ''}`,
                              )
                              .join(', ')
                          : T('Ingen vakt');
                    return (
                      <td key={d} className={`o-cell${d === today ? ' o-today' : ''}`}>
                        <div className="o-chead">
                          <span>{head}</span>
                          {addBtn(T('Ny oppgave for {who} {date}', { who: r.name, date: dayMonth(d) }), {
                            date: d,
                            person: r.id,
                          })}
                        </div>
                        {here.map((t) => taskLine(t))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  } else if (layout === 'grid' && view === 'month') {
    const lead = dow(from);
    body = (
      <div className="o-month" data-testid="opg-month">
        {Array.from({ length: lead }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: empty days before the first have no identity
          <div key={`o${i}`} className="o-out" aria-hidden="true" />
        ))}
        {days.map((d) => (
          <div key={d} className={d === today ? 'o-today' : ''}>
            <div className="o-chead">
              <span>
                <b>{dayHead(d)}</b>
              </span>
              {addBtn(T('Ny oppgave {date}', { date: dayMonth(d) }), { date: d })}
            </div>
            {shown
              .filter((t) => onDay(t, d))
              .map((t) => (
                <button key={t.id} type="button" className="o-ttl o-mt" onClick={() => setDetail(t.id)}>
                  {t.status !== 'open' ? '✓ ' : ''}
                  {t.title}
                </button>
              ))}
          </div>
        ))}
      </div>
    );
  } else {
    const withTasks = days.filter((d) => view === 'day' || shown.some((t) => onDay(t, d)));
    body = withTasks.length ? (
      <div className="o-list" data-testid="opg-list">
        {withTasks.map((d) => (
          <section key={d} aria-label={dayMonth(d)}>
            <h2>
              {`${dayHead(d)} ${dayMonth(d).split(' ')[1] ?? ''}`}
              {addBtn(T('Ny oppgave {date}', { date: dayMonth(d) }), { date: d })}
            </h2>
            {shown.filter((t) => onDay(t, d)).length ? (
              shown
                .filter((t) => onDay(t, d))
                .map((t) => (
                  <div className="o-row" key={t.id}>
                    {taskLine(t, true)}
                  </div>
                ))
            ) : (
              <div className="o-row o-note">{T('Ingen oppgaver denne dagen.')}</div>
            )}
          </section>
        ))}
      </div>
    ) : null;
  }

  const empty = !tasksQ.isPending && !tasksQ.isError && shown.length === 0;
  const steps: [GuideStep, string, string, () => void][] = [
    ['create', T('Lag en oppgave'), T('Hva skal gjøres, og når.'), () => setForm({ date: today })],
    [
      'assign',
      T('Gi den en ansvarlig'),
      T('Én person har ansvaret. Uten vakt er lov.'),
      () => setForm({ date: today }),
    ],
    [
      'follow',
      T('Følg opp'),
      T('Se hva som mangler, og fullfør eller noter.'),
      () => setParam({ status: 'follow' }),
    ],
  ];

  return (
    <div className="opg" data-testid="oppgaver">
      <div className="o-head">
        <h1 tabIndex={-1}>{T('Oppgaver')}</h1>
        <button type="button" className="o-guide" onClick={() => setHelp(true)}>
          <Icon name="help" />
          {T('Kom i gang · {n} av 3 steg', { n: guide.length })}
        </button>
        <span className="o-sp" />
        <div className="o-acts">
          <button type="button" className="o-b o-line" onClick={() => setHelp(true)}>
            {T('Hjelp')}
          </button>
          <Link className="o-b o-line" to="/innstillinger/oppgaver">
            {T('Oppgavetyper')}
          </Link>
          {mayEdit && (
            <button
              type="button"
              className="o-b o-pri"
              onClick={() => setForm({ date: date })}
              data-testid="opg-new"
            >
              <Icon name="plus" />
              {T('Ny oppgave')}
            </button>
          )}
        </div>
      </div>
      {!mayEdit && (
        <p className="o-note">
          {T('Du kan se oppgavene, men rollen din kan ikke opprette eller endre dem.')}
        </p>
      )}

      <div className="o-bar">
        <button
          type="button"
          className="o-b o-line"
          aria-label={T('Forrige')}
          onClick={() => setParam({ dato: step(view, date, -1) })}
        >
          <Icon name="chevronLeft" />
        </button>
        <button
          type="button"
          className="o-b o-line"
          aria-label={T('Neste')}
          onClick={() => setParam({ dato: step(view, date, 1) })}
        >
          <Icon name="chevronRight" />
        </button>
        <input
          type="date"
          className="inp"
          aria-label={T('Dato')}
          value={date}
          onChange={(e) => e.target.value && setParam({ dato: e.target.value })}
        />
        <button type="button" className="o-b o-line" onClick={() => setParam({ dato: today })}>
          {T('I dag')}
        </button>
        <select aria-label={T('Periode')} value={view} onChange={(e) => setParam({ vis: e.target.value })}>
          <option value="day">{T('Dag')}</option>
          <option value="week">{T('Uke')}</option>
          <option value="two">{T('To uker')}</option>
          <option value="month">{T('Måned')}</option>
        </select>
        {!narrow && (
          <>
            <button
              type="button"
              className="o-b o-line"
              aria-pressed={layout === 'list'}
              aria-label={T('Liste')}
              title={T('Liste')}
              onClick={() => setParam({ visning: 'list' })}
            >
              <Icon name="list" />
            </button>
            <button
              type="button"
              className="o-b o-line"
              aria-pressed={layout === 'grid'}
              aria-label={T('Kalender')}
              title={T('Kalender')}
              onClick={() => setParam({ visning: null })}
            >
              <Icon name="grid" />
            </button>
          </>
        )}
        <label className="o-search">
          <Icon name="search" />
          <input
            className="inp"
            type="search"
            aria-label={T('Søk i oppgaver og ansatte')}
            placeholder={T('Søk i oppgaver og ansatte')}
            value={q}
            onChange={(e) => setParam({ sok: e.target.value })}
          />
        </label>
      </div>

      <div className="o-fil">
        {(
          [
            ['all', T('Alle'), n.all],
            ['follow', T('Trenger oppfølging'), n.follow],
            ['done', T('Utført'), n.done],
          ] as [Filter, string, number][]
        ).map(([k, label, c]) => (
          <button
            key={k}
            type="button"
            className="o-b"
            aria-pressed={filter === k}
            onClick={() => setParam({ status: k === 'all' ? null : k })}
          >
            {label} <b>{c}</b>
          </button>
        ))}
        <span className="o-range" data-testid="opg-range">
          {view === 'month' ? monthText(from) : rangeText(from, to)}
        </span>
        <span className="o-meta" aria-live="polite">
          {tasksQ.isSuccess
            ? T('{done} av {all} utført · Aktive oppgaver i valgt periode', {
                done: doneShown,
                all: shown.length,
              })
            : ''}
        </span>
        <button
          type="button"
          className="o-b"
          onClick={download}
          disabled={!tasksQ.isSuccess}
          data-testid="opg-report"
        >
          <Icon name="download" />
          {T('Rapport')}
        </button>
      </div>

      {empty ? (
        q || filter !== 'all' ? (
          <Empty
            title={T('Ingen oppgaver passer søket eller filteret')}
            action={
              <Button small onClick={() => setParam({ sok: null, status: null })}>
                {T('Vis alle i perioden')}
              </Button>
            }
          />
        ) : (
          <Empty
            tone="setup"
            title={T('Ingen oppgaver i perioden')}
            action={
              mayEdit ? (
                <Button small tone="pri" onClick={() => setForm({ date })}>
                  {T('Ny oppgave')}
                </Button>
              ) : undefined
            }
          >
            {T('En oppgave er arbeid med én ansvarlig. Den lager ingen vakt og ingen registrert tid.')}
          </Empty>
        )
      ) : (
        body
      )}

      {form && (
        <TaskForm
          open={form}
          users={users}
          shifts={shifts}
          today={today}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      )}
      {detail != null && !form && (
        <TaskDetail
          id={detail}
          mayEdit={mayEdit}
          today={today}
          onClose={() => {
            setDetail(null);
            if (sp.get('oppgave')) setParam({ oppgave: null });
          }}
          onEdit={(t) => setForm({ task: t })}
          onDeleted={(t, ids) => {
            setDetail(null);
            toast(
              ids.length > 1
                ? T('{n} oppgaver er slettet', { n: ids.length })
                : T('«{title}» er slettet', { title: t.title }),
              () => {
                void w.restore.mutateAsync(t.id).then(() => toast(T('Gjenopprettet')));
              },
            );
          }}
        />
      )}
      <Drawer
        open={help}
        onClose={() => setHelp(false)}
        title={T('Kom i gang med oppgaver')}
        className="opgd"
      >
        <p>{T('Tre steg som bruker siden slik den er. Du kan hoppe over og komme tilbake hit fra Hjelp.')}</p>
        <ol className="o-hist">
          {steps.map(([k, title, text, go]) => (
            <li key={k}>
              <b>
                {guide.includes(k) ? '✓ ' : ''}
                {title}
              </b>
              <small>{text}</small>
              {mayEdit || k === 'follow' ? (
                <Button
                  small
                  onClick={() => {
                    setHelp(false);
                    go();
                  }}
                >
                  {k === 'follow' ? T('Vis det som trenger oppfølging') : T('Ny oppgave')}
                </Button>
              ) : null}
            </li>
          ))}
        </ol>
        <p className="o-hint">
          {T(
            'Fremdriften her gjelder bare veiledningen og følger deg til andre nettlesere. Den sier ingenting om oppgavene er utført, og ingen får e-post eller varsel.',
          )}
        </p>
      </Drawer>
    </div>
  );
}
